import React from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "../ui/dropdown-menu";
import PaginatedTable, { PaginatedTableColumn } from "../ui/paginated-table";
import { Progress } from "../ui/progress";
import { HistoryIcon, Loader2Icon, MoreVerticalIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import moment from "moment";

interface Review {
  id: number;
  date_created: number;
  date_completed?: number;
  num_reviews: number;
  num_corrections: number;
  status: string;
  review_type?: string;
  meta?: {
    review_type?: string;
  };
}

interface ReviewsTableProps {
  reviews: Review[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onRetryReview: (reviewId: number) => void;
  onForceProlificSync: (reviewId: number) => void;
  onReviewClick?: (review: Review) => void;
  syncingReviewIds?: Set<number>;
  reviewProgress: Record<string, any>;
  emptyTitle?: string;
  emptyDescription?: string;
}

const ReviewsTable: React.FC<ReviewsTableProps> = ({
  reviews,
  loading,
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onRetryReview,
  onForceProlificSync,
  onReviewClick,
  syncingReviewIds = new Set(),
  reviewProgress,
  emptyTitle = "No reviews found",
  emptyDescription = "Start a review to see results here."
}) => {
  const formatDate = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const getStatusTagColor = (status: string): string => {
    switch (status?.toUpperCase()?.trim()) {
      case "CREATED":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
      case "COMPLETED":
      case "COMPLETE":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
      case "FAILED":
      case "ERROR":
        return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";
      case "PENDING":
      case "PROCESSING":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
    }
  };

  const renderStatus = (review: Review) => {
    const progressData = reviewProgress[review.id.toString()];
    const currentStatus = progressData?.status || review.status;

    if (currentStatus !== "RUNNING" && currentStatus !== "PROCESSING") {
      const completed_count =
        progressData?.corrected_count +
        progressData?.good_count +
        progressData?.error_count;
      const tooltipContent =
        (currentStatus === "ERROR" ||
          currentStatus === "COMPLETE" ||
          currentStatus === "COMPLETED") &&
        progressData
          ? `${completed_count}/${progressData.total_count} reviews completed (${progressData.corrected_count} corrected, ${progressData.good_count} good, ${progressData.error_count} errors)`
          : undefined;

      return (
        <div title={tooltipContent}>
          <span className={cn(
            "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
            getStatusTagColor(currentStatus)
          )}>
            {currentStatus.toUpperCase()}
          </span>
        </div>
      );
    }

    if (!progressData) {
      return (
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
          <span className="text-sm text-muted-foreground">Processing...</span>
        </div>
      );
    }

    const { total_count, corrected_count, good_count, error_count } = progressData;
    const completed_count = corrected_count + good_count + error_count;
    const percent = total_count > 0 ? (completed_count / total_count) * 100 : 0;

    return (
      <div className="w-24">
        <Progress
          value={Number(percent.toFixed(1))}
          className="h-2"
        />
        <div className="text-xs text-muted-foreground mt-1">
          {`${completed_count}/${total_count}`}
        </div>
      </div>
    );
  };

  const canRetry = (status: string) => {
    return ["FAILED", "COMPLETED", "SCHEDULED", "CANCELLED"].includes(status.toUpperCase());
  };

  const hasProcessingReview = reviews.some((review) => review.status === "PROCESSING");

  const getReviewTypeBadgeInfo = (type: string) => {
    const t = type?.toLowerCase() || "ai";
    switch (t) {
      case "ai":
        return {
          label: "AI",
          className:
            "border-purple-500/30 text-purple-700 bg-purple-50 dark:bg-purple-950 dark:text-purple-300"
        };
      case "human":
      case "expert":
        return {
          label: "Expert",
          className:
            "border-indigo-500/30 text-indigo-700 bg-indigo-50 dark:bg-indigo-950 dark:text-indigo-300"
        };
      case "mixed":
        return {
          label: "Mixed",
          className:
            "border-blue-500/30 text-blue-700 bg-blue-50 dark:bg-blue-950 dark:text-blue-300"
        };
      default:
        return {
          label: t,
          className:
            "border-gray-500/30 text-gray-700 bg-gray-50 dark:bg-gray-950 dark:text-gray-300"
        };
    }
  };

  const columns: PaginatedTableColumn<Review>[] = [
    {
      key: "meta",
      header: "Type",
      width: "w-[12%]",
      render: (review) => {
        const type = review.review_type || review.meta?.review_type || "ai";
        const { label, className } = getReviewTypeBadgeInfo(type);
        return (
          <Badge variant="outline" className={cn("text-xs capitalize", className)}>
            {label}
          </Badge>
        );
      }
    },
    {
      key: "date_created",
      header: "Date Started",
      width: "w-[18%]",
      render: (review) => (
        <div className="space-y-1">
          <div className="text-sm font-gc-reg text-foreground">
            {formatDate(review.date_created)}
          </div>
          <div className="text-xs text-muted-foreground">
            {formatTime(review.date_created)}
          </div>
        </div>
      )
    },
    {
      key: "date_completed",
      header: "Date Completed",
      width: "w-[18%]",
      render: (review) => (
        review.date_completed ? (
          <div className="space-y-1">
            <div className="text-sm font-gc-reg text-foreground">
              {formatDate(review.date_completed)}
            </div>
            <div className="text-xs text-muted-foreground">
              {formatTime(review.date_completed)}
            </div>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )
      )
    },
    {
      key: "num_reviews",
      header: "# Reviews",
      width: "w-[12%]",
      render: (review) => (
        <span className="text-sm font-gc-med text-foreground">
          {review.num_reviews}
        </span>
      )
    },
    {
      key: "num_corrections",
      header: "# Corrections",
      width: "w-[12%]",
      render: (review) => (
        <span className="text-sm font-gc-med text-foreground">
          {review.num_corrections}
        </span>
      )
    },
    {
      key: "status",
      header: "Status",
      width: "w-[18%]",
      render: renderStatus
    },
    {
      key: "id",
      header: "Actions",
      width: "w-[10%]",
      render: (review) => {
        const reviewType = (review.review_type || review.meta?.review_type || "").toLowerCase();
        const isSyncing = syncingReviewIds.has(review.id);
        const canForceSync = reviewType === "human" || reviewType === "mixed";
        const canRetryAction =
          !(
            review.status === "PROCESSING" ||
            review.status === "RUNNING" ||
            hasProcessingReview
          ) && canRetry(review.status);
        const hasActions = canRetryAction || canForceSync;
        if (!hasActions) {
          return <span className="text-sm text-muted-foreground">—</span>;
        }
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground transition-colors"
                onClick={(event) => event.stopPropagation()}
                disabled={isSyncing}
              >
                {isSyncing ? (
                  <Loader2Icon className="w-4 h-4 animate-spin" />
                ) : (
                  <MoreVerticalIcon className="w-4 h-4" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
              {canRetryAction && (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onSelect={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onRetryReview(review.id);
                  }}
                >
                  <HistoryIcon className="w-4 h-4 mr-2" />
                  Retry
                </DropdownMenuItem>
              )}
              {canForceSync && (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onSelect={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onForceProlificSync(review.id);
                  }}
                  disabled={isSyncing}
                >
                  Refresh status
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      }
    }
  ];

  return (
    <PaginatedTable<Review>
      data={reviews}
      columns={columns}
      loading={loading}
      currentPage={currentPage}
      onPageChange={onPageChange}
      pageSize={itemsPerPage}
      totalItems={totalItems}
      serverSidePagination={true}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
      rowClassName={cn(
        "transition-colors",
        onReviewClick ? "cursor-pointer hover:bg-accent/20" : ""
      )}
      onRowClick={onReviewClick}
    />
  );
};

export default ReviewsTable; 